import React, { useState, useCallback } from 'react';
import { BrandMark } from './ui/Logo';
import Icon from './ui/Icon';
import ClientsSection from './ClientsSection';
import ChipsSection from './ChipsSection';
import RepairsSection from './RepairsSection';
import KitsSection from './KitsSection';
import ShoppingSection from './ShoppingSection';
import SettingsSection from './SettingsSection';

type TabId = 'clients' | 'chips' | 'repairs' | 'kits' | 'shopping' | 'settings';

interface NavTab {
  id: TabId;
  label: string;
  icon: string;
  // Нижняя панель узкая: длинному названию нужен короткий вариант.
  short?: string;
}

const NAV_TABS: NavTab[] = [
  { id: 'clients', label: 'Клиенты', icon: 'clients' },
  { id: 'chips', label: 'Чипы', icon: 'chips' },
  { id: 'repairs', label: 'Ремонты', icon: 'repairs' },
  { id: 'kits', label: 'Комплектация', icon: 'box', short: 'Комплект' },
  { id: 'shopping', label: 'Покупки', icon: 'cart' },
  { id: 'settings', label: 'Настройки', icon: 'settings' },
];

interface MainAppProps {
  onLogout: () => void;
}

const MainApp: React.FC<MainAppProps> = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const saved = localStorage.getItem('activeTabNew') as TabId | null;
    // Архив переехал внутрь «Ремонтов» — сохранённая вкладка может больше не существовать.
    return NAV_TABS.some(t => t.id === saved) ? (saved as TabId) : 'repairs';
  });
  const [filterClientId, setFilterClientId] = useState<number | null>(null);
  const [openRepairId, setOpenRepairId] = useState<number | null>(null);
  const [openKitId, setOpenKitId] = useState<number | null>(null);
  const [openKitArchived, setOpenKitArchived] = useState(false);
  const [openArchiveRepairId, setOpenArchiveRepairId] = useState<number | null>(null);
  const [theme, setThemeState] = useState<string>(() => {
    return localStorage.getItem('theme') || 'dark';
  });

  const handleTabChange = (id: TabId) => {
    if (id !== activeTab) {
      setActiveTab(id);
      localStorage.setItem('activeTabNew', id);
    }
  };

  const handleViewRepairs = (clientId: number) => {
    setFilterClientId(clientId);
    setActiveTab('repairs');
    localStorage.setItem('activeTabNew', 'repairs');
  };

  const handleClearFilter = () => setFilterClientId(null);

  const handleOpenRepair = useCallback((repairId: number, archived: boolean) => {
    setFilterClientId(null);
    if (archived) setOpenArchiveRepairId(repairId);
    else setOpenRepairId(repairId);
    setActiveTab('repairs');
    localStorage.setItem('activeTabNew', 'repairs');
  }, []);

  const handleArchiveRepairOpened = useCallback(() => setOpenArchiveRepairId(null), []);

  const handleRepairOpened = useCallback(() => setOpenRepairId(null), []);

  const handleOpenKit = useCallback((kitId: number, archived: boolean) => {
    setOpenKitId(kitId);
    setOpenKitArchived(archived);
    setActiveTab('kits');
    localStorage.setItem('activeTabNew', 'kits');
  }, []);

  const handleKitOpened = useCallback(() => setOpenKitId(null), []);

  const handleThemeChange = (t: string) => {
    setThemeState(t);
    localStorage.setItem('theme', t);
    document.documentElement.setAttribute('data-theme', t);
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    onLogout();
  };

  const renderSection = () => {
    switch (activeTab) {
      case 'clients':
        return <ClientsSection onViewRepairs={handleViewRepairs} />;
      case 'chips':
        return <ChipsSection />;
      case 'repairs':
        return (
          <RepairsSection
            filterClientId={filterClientId}
            onClearFilter={handleClearFilter}
            openRepairId={openRepairId}
            onRepairOpened={handleRepairOpened}
            onOpenKit={handleOpenKit}
            openArchiveRepairId={openArchiveRepairId}
            onArchiveRepairOpened={handleArchiveRepairOpened}
          />
        );
      case 'kits':
        return <KitsSection onOpenRepair={handleOpenRepair} openKitId={openKitId} openKitArchived={openKitArchived} onKitOpened={handleKitOpened} />;
      case 'shopping':
        return <ShoppingSection />;
      case 'settings':
        return <SettingsSection theme={theme} setTheme={handleThemeChange} onLogout={handleLogout} />;
      default:
        return null;
    }
  };

  const Sidebar: React.FC<{ width?: number }> = ({ width = 260 }) => (
    <div className="cb-sidebar" style={{ width }}>
      <div style={{ padding: width > 220 ? '24px 20px 16px' : '18px 14px 12px', display: 'flex', justifyContent: 'center' }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '10px 20px',
          borderRadius: 'var(--r-md)',
          border: '1px solid var(--teal-mid)',
          background: 'var(--teal-dim)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          boxShadow: '0 0 18px rgba(45,212,191,0.12)',
        }}>
          <BrandMark size={width > 220 ? 'lg' : 'md'} />
        </div>
      </div>
      <nav style={{ flex: 1, padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {NAV_TABS.map(t => {
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => handleTabChange(t.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 12px',
                borderRadius: 'var(--r-sm)',
                border: active ? '2px solid transparent' : '2px solid transparent',
                cursor: 'pointer',
                background: active ? 'var(--nav-active-bg)' : 'transparent',
                color: active ? 'var(--nav-active-color)' : 'var(--nav-color)',
                fontFamily: 'var(--sans)',
                fontSize: 14,
                fontWeight: active ? 600 : 400,
                textAlign: 'left',
                transition: 'all 0.15s',
                backdropFilter: active ? 'blur(12px)' : 'none',
                WebkitBackdropFilter: active ? 'blur(12px)' : 'none',
                borderLeft: active ? '2px solid var(--teal)' : '2px solid transparent',
              }}
            >
              <Icon name={t.icon} size={17} color={active ? 'var(--teal)' : 'var(--nav-color)'} />
              {t.label}
            </button>
          );
        })}
      </nav>
      <div style={{
        padding: '16px 18px',
        borderTop: '1px solid var(--sidebar-border)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}>
        <div style={{
          width: 30,
          height: 30,
          borderRadius: 15,
          background: 'var(--teal-dim)',
          border: '1px solid var(--teal-mid)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
          fontWeight: 700,
          color: 'var(--teal)',
        }}>И</div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text0)' }}>Игорь</div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>admin</div>
        </div>
      </div>
    </div>
  );

  const BottomBar: React.FC = () => (
    <div className="cb-bottom-bar">
      {NAV_TABS.map(t => {
        const active = activeTab === t.id;
        return (
          <button
            key={t.id}
            onClick={() => handleTabChange(t.id)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 5,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px 0',
              color: active ? 'var(--teal)' : 'var(--nav-color)',
              transition: 'color 0.15s',
            }}
          >
            <Icon name={t.icon} size={22} color={active ? 'var(--teal)' : 'var(--nav-color)'} />
            <span style={{ fontSize: 10, fontWeight: active ? 700 : 400 }}>{t.short || t.label}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="cb-app cb-canvas">
      <div className="cb-layout">
        <Sidebar />
        <div className="cb-content">
          {renderSection()}
        </div>
      </div>
      <BottomBar />
    </div>
  );
};

export default MainApp;
